// #130 Sevkiyat Planlama / Sipariş Planla & Dönüştür — ortak yardımcılar.
// Tüm API yanıtları { success, data, message } sarmalı; .data okunur.
export const API_BASE = '/api/shipmentplans';

// ApiResponse<T> sarmalayıcısını açar.
export function unwrap(payload) {
  if (payload && typeof payload === 'object' && 'data' in payload) return payload.data;
  if (payload && typeof payload === 'object' && 'Data' in payload) return payload.Data;
  return payload;
}

export function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

export function apiMessage(payload, fallback) {
  return pick(payload, 'message', 'Message') || fallback;
}

// ── Belge türü (Fat. / İrs.) ───────────────────────────────────────────────
// DEĞERLER backend + müşteri master ile aynı sözlükte: FATURA | IRSALIYE.
export const DOCUMENT_TYPES = [
  { value: 'FATURA', short: 'Fat.', label: 'Fatura' },
  { value: 'IRSALIYE', short: 'İrs.', label: 'İrsaliye' },
];
export function docTypeShort(v) {
  return DOCUMENT_TYPES.find((d) => d.value === v)?.short || 'Fat.';
}
export function nextDocType(v) {
  return v === 'IRSALIYE' ? 'FATURA' : 'IRSALIYE';
}

// ── E-Belge türü → rozet meta (logo metni + renk) ──────────────────────────
export const E_DOCUMENT_META = {
  EINVOICE: { label: 'E-Fatura', color: '#0073ea', bg: 'rgba(0,115,234,0.12)' },
  EINVOICE_EDISPATCH: { label: 'E-Fatura + E-İrsaliye', short: 'E-Fat+İrs', color: '#7a3cff', bg: 'rgba(122,60,255,0.12)' },
  EARCHIVE: { label: 'E-Arşiv', color: '#16a34a', bg: 'rgba(22,163,74,0.12)' },
};
export function eDocMeta(code) {
  if (!code) return null;
  return E_DOCUMENT_META[code] || { label: code, color: '#64748b', bg: 'rgba(100,116,139,0.12)' };
}

// ── Tarih yardımcıları (yerel, <input type=date> uyumlu) ───────────────────
export function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return isoDate(d);
}
export function fromDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}
/**
 * #807 Aralık ÜST sınırı için: seçilen günün YEREL gün sonu (23:59:59.999).
 *
 * `fromDateInput` gün BAŞINI üretir; belge tarihleri ise saat taşır (OrderHeader.DocumentDate
 * DateTimeOffset'tir). Bitişe gün başı gönderilince "28.07" seçen kullanıcı 28.07 saat 09:15'te
 * girilmiş siparişi göremiyor, bitişi +1 gün vermek zorunda kalıyordu.
 *
 * Neden burada (yerel) ve backend'de `.Date.AddDays(1)` DEĞİL: `toISOString()` yerel gece yarısını
 * UTC'ye kaydırıyor (TR'de −3 sa), dolayısıyla sunucu tarafında gün almak bir gün GERİ kaydırır.
 * Yerel gün sonunu burada üretmek doğru anı verir.
 */
export function toDayEndInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T23:59:59.999`).toISOString(); } catch { return null; }
}
export function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR');
}

export function fmtMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0,00';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
export function fmtNumber(value, frac = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: frac });
}

// ── Plan durum meta ─────────────────────────────────────────────────────────
export const PLAN_STATUS_META = {
  DRAFT: { label: 'Taslak', tone: 'neutral' },
  PLANNED: { label: 'Planlandı', tone: 'wait' },
  CONVERTED: { label: 'Dönüştürüldü', tone: 'ok' },
  CANCELLED: { label: 'İptal', tone: 'bad' },
};
export function planStatusMeta(status) {
  return PLAN_STATUS_META[(status || '').toUpperCase()] || { label: status || '—', tone: 'neutral' };
}

// ── Doluluk oranı rengi (ağırlık/hacim bar) ────────────────────────────────
export function fillColor(ratio) {
  if (ratio > 1) return '#e2445c';      // taşma — kırmızı
  if (ratio > 0.85) return '#fdab3d';   // dolmak üzere — amber
  return '#00c875';                     // uygun — yeşil
}

// ── Distribütör / depo SearchableSelect fetchOptions üreticisi ─────────────
// Sunucu-aramalı (OData) lookup. endpoint = '/api/distributors' gibi.
// baseFilter: her istekte uygulanan OData koşulu (ör. depo → `VendorId eq <guid>`);
// arama metni varsa onunla AND'lenir.
export function makeLookupFetch(endpoint, { authHeaders, labelField = 'name', codeField = 'textId', baseFilter = null } = {}) {
  return async (query, skip = 0) => {
    const top = 20;
    const params = [`$top=${top}`, `$skip=${skip || 0}`];
    const clauses = [];
    if (baseFilter) clauses.push(`(${baseFilter})`);
    if (query && query.trim()) {
      const q = query.trim().replace(/'/g, "''");
      clauses.push(`(contains(${labelField},'${q}') or contains(${codeField},'${q}'))`);
    }
    if (clauses.length) params.push(`$filter=${encodeURIComponent(clauses.join(' and '))}`);
    params.push(`$orderby=${labelField}`);
    const url = `${endpoint}?${params.join('&')}`;
    try {
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) return [];
      const data = await res.json();
      const rows = data?.value ?? data?.data ?? data?.Data ?? (Array.isArray(data) ? data : []);
      return rows.map((r) => ({
        value: r.id ?? r.Id,
        label: [r[codeField] ?? r[capitalize(codeField)], r[labelField] ?? r[capitalize(labelField)]]
          .filter(Boolean).join(' · ') || (r[labelField] ?? r.id),
      }));
    } catch {
      return [];
    }
  };
}
function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

// ── Araç doluluk toplamları ────────────────────────────────────────────────
export function vehicleTotals(vehicle) {
  const orders = vehicle.orders || [];
  const weight = orders.reduce((s, o) => s + (Number(o.totalWeight) || 0), 0);
  const volume = orders.reduce((s, o) => s + (Number(o.totalVolume) || 0), 0);
  const amount = orders.reduce((s, o) => s + (Number(o.netAmount) || 0), 0);
  return { weight, volume, amount, count: orders.length };
}

// ── Order DTO normalize ────────────────────────────────────────────────────
export function normalizeOrder(o) {
  return {
    orderId: o.orderId ?? o.OrderId,
    customerId: o.customerId ?? o.CustomerId,
    customerName: o.customerName ?? o.CustomerName ?? '—',
    // #141 alt liste kolonları + arama alanları.
    customerCode: o.customerCode ?? o.CustomerCode ?? '',
    shortName: o.shortName ?? o.ShortName ?? '',
    neighborhood: o.neighborhood ?? o.Neighborhood ?? '',
    street: o.street ?? o.Street ?? '',
    address: o.address ?? o.Address ?? '',
    salesRepName: o.salesRepName ?? o.SalesRepName ?? '',
    customerLat: Number(o.customerLat ?? o.CustomerLat),
    customerLng: Number(o.customerLng ?? o.CustomerLng),
    documentDate: o.documentDate ?? o.DocumentDate,
    netAmount: Number(o.netAmount ?? o.NetAmount) || 0,
    grossAmount: Number(o.grossAmount ?? o.GrossAmount) || 0,
    documentNo: o.documentNo ?? o.DocumentNo ?? o.textId ?? o.TextId ?? '',
    defaultDocumentType: o.defaultDocumentType ?? o.DefaultDocumentType ?? 'FATURA',
    documentType: o.documentType ?? o.DocumentType ?? o.defaultDocumentType ?? o.DefaultDocumentType ?? 'FATURA',
    eDocumentType: o.eDocumentType ?? o.EDocumentType ?? null,
    // #152 madde 10 — müşteri E-Fatura / E-İrsaliye mükellefi mi (araç içi listede simge).
    isEinvoice: o.isEinvoice ?? o.IsEinvoice ?? false,
    isEdispatch: o.isEdispatch ?? o.IsEdispatch ?? false,
    warehouseId: o.warehouseId ?? o.WarehouseId ?? null,
    totalWeight: Number(o.totalWeight ?? o.TotalWeight) || 0,
    totalVolume: Number(o.totalVolume ?? o.TotalVolume) || 0,
    assignedVehicleId: o.assignedVehicleId ?? o.AssignedVehicleId ?? null,
    visitOrderNo: o.visitOrderNo ?? o.VisitOrderNo ?? null,
  };
}

export function normalizeVehicle(v) {
  return {
    id: v.id ?? v.Id ?? null,
    shipmentVehicleId: v.shipmentVehicleId ?? v.ShipmentVehicleId ?? v.id ?? v.Id,
    plaka: v.plaka ?? v.Plaka ?? v.plate ?? '—',
    brand: v.brand ?? v.Brand ?? '',
    model: v.model ?? v.Model ?? '',
    maxWeight: Number(v.maxWeight ?? v.MaxWeight) || 0,
    volume: Number(v.volume ?? v.Volume) || 0,
    vendorId: v.vendorId ?? v.VendorId ?? null,
    distributorId: v.distributorId ?? v.DistributorId ?? null,
    distributorName: v.distributorName ?? v.DistributorName ?? '',
    orders: [],
  };
}

// ── Sipariş listesi arama (#141) ────────────────────────────────────────────
// Mahalle, müşteri kodu, ünvan, kısa ad (tabela), adres, sokak, satış temsilcisi.
export function orderMatchesQuery(order, needle) {
  if (!needle) return true;
  const hay = [
    order.customerName, order.customerCode, order.shortName,
    order.neighborhood, order.street, order.address, order.salesRepName,
    order.documentNo,
  ].filter(Boolean).join(' ').toLocaleLowerCase('tr-TR');
  return hay.includes(needle);
}
export function filterOrders(orders, query) {
  const needle = (query || '').trim().toLocaleLowerCase('tr-TR');
  if (!needle) return orders;
  return orders.filter((o) => orderMatchesQuery(o, needle));
}

// ── Trafik analizi (sezgisel, saat-bazlı) ───────────────────────────────────
// OSRM serbest-akış süresine tipik şehir-içi gün-saat yoğunluk çarpanı uygular.
// CANLI trafik DEĞİLDİR; planlama için tahminî bir göstergedir.
// Saat (0-23) → çarpan. Sabah (07-09) ve akşam (17-19) zirveleri.
const TRAFFIC_HOURLY = [
  1.00, 1.00, 1.00, 1.00, 1.00, 1.05, // 00-05
  1.15, 1.40, 1.65, 1.45, 1.20, 1.15, // 06-11
  1.20, 1.15, 1.15, 1.20, 1.35, 1.60, // 12-17
  1.70, 1.45, 1.20, 1.10, 1.05, 1.00, // 18-23
];

export function trafficFactorForHour(hour, weekend) {
  const h = ((Math.floor(hour) % 24) + 24) % 24;
  const base = TRAFFIC_HOURLY[h];
  // Hafta sonu: yoğunluğun serbest-akış üstü kısmını yarıya indir.
  return weekend ? 1 + (base - 1) * 0.5 : base;
}

export function trafficLevel(factor) {
  if (factor >= 1.4) return { label: 'Yoğun', color: 'var(--danger)', emoji: '🔴' };
  if (factor >= 1.15) return { label: 'Orta', color: 'var(--warn)', emoji: '🟠' };
  return { label: 'Akıcı', color: '#16a34a', emoji: '🟢' };
}

function parseHM(hm) {
  const [h, m] = String(hm || '09:00').split(':').map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
}
function toHM(totalMin) {
  const t = ((Math.round(totalMin) % 1440) + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}
const pad2 = (n) => String(n).padStart(2, '0');

// Serbest-akış süresi boyunca saatlik çarpanı ağırlıklı ortalama alır
// (yolculuk yoğun saate denk gelirse etkisini doğru yansıtır).
function averagedFactor(startMin, freeFlowMin, weekend) {
  if (freeFlowMin <= 0) return trafficFactorForHour(Math.floor(startMin / 60), weekend);
  let remaining = freeFlowMin;
  let t = startMin;
  let weighted = 0;
  let guard = 0;
  while (remaining > 0 && guard++ < 200) {
    const hour = Math.floor(t / 60);
    const minsLeft = 60 - (t % 60);
    const chunk = Math.min(remaining, minsLeft);
    weighted += chunk * trafficFactorForHour(hour, weekend);
    t += chunk;
    remaining -= chunk;
  }
  return weighted / freeFlowMin;
}

// km + çıkış saati → trafik tahmini, durum, varış, en uygun çıkış ve kaçınılacak pencere.
export function analyzeTraffic({ km, departure = '09:00', weekend = false, avgSpeed = 40 }) {
  const freeFlowMin = avgSpeed > 0 ? ((Number(km) || 0) / avgSpeed) * 60 : 0;
  const startMin = parseHM(departure);
  const factor = averagedFactor(startMin, freeFlowMin, weekend);
  const trafficMin = freeFlowMin * factor;
  const level = trafficLevel(factor);
  const arrival = toHM(startMin + trafficMin);

  // En uygun çıkış saati (00:00–23:00 tara, en kısa trafikli süre).
  let best = null;
  for (let h = 0; h < 24; h += 1) {
    const f = averagedFactor(h * 60, freeFlowMin, weekend);
    const mins = freeFlowMin * f;
    if (!best || mins < best.min) best = { time: `${pad2(h)}:00`, min: mins };
  }

  // Kaçınılacak en yoğun pencere (zirve saati etrafında 'Yoğun' bandı).
  let peakH = 0;
  let peakF = 0;
  for (let h = 0; h < 24; h += 1) {
    const f = trafficFactorForHour(h, weekend);
    if (f > peakF) { peakF = f; peakH = h; }
  }
  let avoid = null;
  if (peakF >= 1.4) {
    let a = peakH;
    let b = peakH;
    while (a > 0 && trafficFactorForHour(a - 1, weekend) >= 1.4) a -= 1;
    while (b < 23 && trafficFactorForHour(b + 1, weekend) >= 1.4) b += 1;
    avoid = `${pad2(a)}:00–${pad2(b + 1)}:00`;
  }

  return { freeFlowMin, trafficMin, factor, level, arrival, best, avoid };
}
